import { createLine, nowIso, type AttachmentRef, type MemoryCard, type PresentationKind, type ReminderSpec } from './model';

export interface CardDraft {
  id?: string;
  title: string;
  pinned?: boolean;
  presentationKind: PresentationKind;
  fields: Record<string, string>;
  collectionIds?: string[];
  reminder?: ReminderSpec;
  attachmentRefs?: AttachmentRef[];
}

const FIELD_CONFIG: Record<PresentationKind, Array<{
  key: string;
  label: string;
  semanticType: Parameters<typeof createLine>[2];
  indexable: boolean;
  concealed: boolean;
}>> = {
  note: [
    { key: 'body', label: 'Body', semanticType: 'multiline', indexable: true, concealed: false },
  ],
  password: [
    { key: 'username', label: 'Username', semanticType: 'username', indexable: false, concealed: false },
    { key: 'password', label: 'Password', semanticType: 'password', indexable: false, concealed: true },
    { key: 'website', label: 'Website', semanticType: 'url', indexable: true, concealed: false },
    { key: 'notes', label: 'Notes', semanticType: 'multiline', indexable: true, concealed: false },
  ],
  card: [
    { key: 'number', label: 'Card number', semanticType: 'card-number', indexable: false, concealed: true },
    { key: 'expiry', label: 'Expiry', semanticType: 'date', indexable: true, concealed: false },
    { key: 'securityCode', label: 'Security code', semanticType: 'security-code', indexable: false, concealed: true },
    { key: 'pin', label: 'PIN', semanticType: 'pin', indexable: false, concealed: true },
    { key: 'notes', label: 'Notes', semanticType: 'multiline', indexable: true, concealed: false },
  ],
  photoId: [
    { key: 'documentType', label: 'Document type', semanticType: 'text', indexable: true, concealed: false },
    { key: 'number', label: 'Document number', semanticType: 'id-number', indexable: false, concealed: true },
    { key: 'expiry', label: 'Expiry', semanticType: 'date', indexable: true, concealed: false },
    { key: 'notes', label: 'Notes', semanticType: 'multiline', indexable: true, concealed: false },
  ],
  general: [],
};

export function cardFromDraft(draft: CardDraft, existing?: MemoryCard): MemoryCard {
  const timestamp = nowIso();
  const recordId = existing?.recordId ?? draft.id ?? crypto.randomUUID();
  const existingByLabel = new Map(existing?.lines.map((line) => [line.label, line]));
  const lines = FIELD_CONFIG[draft.presentationKind]
    .map((config, position) => {
      const previous = existingByLabel.get(config.label);
      return createLine(config.label, draft.fields[config.key] ?? '', config.semanticType, {
        lineId: previous?.lineId,
        indexable: config.indexable,
        concealed: config.concealed,
        position,
      });
    })
    .filter((line) => line.value.trim().length > 0 || line.semanticType === 'password' || line.label === 'Body');

  return {
    schemaVersion: 1,
    recordId,
    presentationKind: draft.presentationKind,
    title: draft.title.trim(),
    pinned: draft.pinned ?? existing?.pinned ?? false,
    lines,
    collectionIds: [...new Set(draft.collectionIds ?? existing?.collectionIds ?? [])],
    reminder: draft.reminder,
    attachmentRefs: draft.attachmentRefs ?? existing?.attachmentRefs ?? [],
    clientMetadata: {
      createdAt: existing?.clientMetadata.createdAt ?? timestamp,
      updatedAt: timestamp,
    },
  };
}

export function fieldValue(card: MemoryCard, label: string): string {
  return card.lines.find((line) => line.label === label)?.value ?? '';
}

export function cardSummary(card: MemoryCard): string {
  if (card.presentationKind === 'password') {
    return fieldValue(card, 'Username') || fieldValue(card, 'Website') || 'Password';
  }
  if (card.presentationKind === 'card') {
    const number = fieldValue(card, 'Card number').replace(/\s+/g, '');
    return number ? `Ends in ${number.slice(-4)}` : 'Card';
  }
  if (card.presentationKind === 'photoId') {
    return fieldValue(card, 'Document type') || 'Photo ID';
  }
  const firstLine = fieldValue(card, 'Body').trim().split('\n')[0];
  return firstLine || 'Note';
}

export function cardTypeLabel(kind: PresentationKind): string {
  const labels: Record<PresentationKind, string> = {
    note: 'Note',
    password: 'Password',
    card: 'Card',
    photoId: 'Photo ID',
    general: 'Item',
  };
  return labels[kind];
}
